/** PHP 64-bit integers and promoted doubles during pure JSON-input arithmetic.
 * Bigints stay internal: public JSON results project back to JS numbers. Native
 * PHP types/integers already lost before JSON serialization cannot be recovered.
 */
export type PhpArithmetic = bigint | number;
const minimum = -9223372036854775808n, maximum = 9223372036854775807n;
const within = (value: bigint): boolean => value >= minimum && value <= maximum;

/** Cast an actual double, including PHP's modulo-64-bit out-of-range behavior. */
export function phpFloatToInteger(value: number): bigint {
    return Number.isFinite(value) ? BigInt.asIntN(64, BigInt(Math.trunc(value))) : 0n;
}
export function phpJsonInteger(value: unknown): bigint {
    if (value == null || value === false) return 0n;
    if (value === true) return 1n;
    if (typeof value === 'object') return Object.keys(value).length ? 1n : 0n;
    if (typeof value === 'string') {
        const token = value.match(/^[\t\n\r\v\f ]*([+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)/i)?.[1];
        if (!token) return 0n;
        if (/^[+-]?\d+$/.test(token)) {
            const integer = BigInt(token);
            return integer > maximum ? maximum : integer < minimum ? minimum : integer;
        }
        const numeric = Number(token);
        if (!Number.isFinite(numeric)) return 0n;
        if (numeric >= 9223372036854775808) return maximum;
        if (numeric <= -9223372036854775808) return minimum;
        return BigInt(Math.trunc(numeric));
    }
    if (typeof value !== 'number' || !Number.isFinite(value)) throw new TypeError('PHP integer conversion requires finite JSON numbers');
    const lexeme = JSON.stringify(value);
    if (/^-?\d+$/.test(lexeme)) {
        const integer = BigInt(lexeme);
        if (within(integer)) return integer;
    }
    return phpFloatToInteger(value);
}
export function phpAdd(left: PhpArithmetic, right: PhpArithmetic): PhpArithmetic {
    if (typeof left === 'bigint' && typeof right === 'bigint') {
        const result = left + right;
        if (within(result)) return result;
    }
    return Number(left) + Number(right);
}
export function phpSubtract(left: PhpArithmetic, right: PhpArithmetic): PhpArithmetic {
    if (typeof left === 'bigint' && typeof right === 'bigint') {
        const result = left - right;
        if (within(result)) return result;
    }
    return Number(left) - Number(right);
}
export function phpMultiply(left: PhpArithmetic, right: PhpArithmetic): PhpArithmetic {
    if (typeof left === 'bigint' && typeof right === 'bigint') {
        const result = left * right;
        if (within(result)) return result;
    }
    return Number(left) * Number(right);
}
export function phpDivide(left: PhpArithmetic, right: PhpArithmetic): PhpArithmetic {
    if (Number(right) === 0) throw new RangeError('PHP division by zero');
    if (typeof left === 'bigint' && typeof right === 'bigint' && left % right === 0n) {
        const result = left / right;
        if (within(result)) return result;
    }
    return Number(left) / Number(right);
}
export function phpLess(left: PhpArithmetic, right: PhpArithmetic): boolean {
    return typeof left === 'bigint' && typeof right === 'bigint' ? left < right : Number(left) < Number(right);
}
export const phpMin = (left: PhpArithmetic, right: PhpArithmetic): PhpArithmetic => phpLess(right, left) ? right : left;
export const phpMax = (left: PhpArithmetic, right: PhpArithmetic): PhpArithmetic => phpLess(left, right) ? right : left;
